import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  MenuItem,
  Radio,
} from "@mui/material";
import {
  DataTable,
  DataTableColumn,
} from "../../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
} from "../../../../common/dashboard/FilterBar";
import {
  RowActionDescriptor,
  RowActionMenu,
  RowActionsTrigger,
} from "../../../../common/dashboard/RowActionMenu";
import { cmpLongDateTimeFormatter } from "../../../../common/dashboard/formatters";
import { AnchoredMenu } from "../../../../common/dashboard/AnchoredMenu";
import {
  ALL_PERMISSION_LEAVES,
  getPermissionLabel,
  getSelectedPermissionCount,
} from "./permissionCatalogue";
import { ChamberStaffUserViewModel } from "./types";
import {
  getStaffCredentialsActionLabel,
  staffCredentialsNeverIssued,
} from "./staffCredentialsFile";
import { isPermanentlyDisabledStaffUser } from "./staffUserIdentity";
import { applyCmpOrganizationNoun } from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";

export type StaffUserAction =
  | "update"
  | "credentials"
  | "suspend"
  | "enable"
  | "delete"
  | "viewPermissions";

export type StaffUsersTableProps = {
  users: readonly ChamberStaffUserViewModel[];
  loading: boolean;
  error?: string;
  actionLoadingUserId?: string | null;
  onRetry: () => void;
  onAction: (
    action: StaffUserAction,
    user: ChamberStaffUserViewModel
  ) => void;
};

type StaffUserColumn =
  | "name"
  | "jobTitle"
  | "email"
  | "permissions"
  | "status"
  | "lastLogin"
  | "actions";

type StatusFilter = "ALL" | "ACTIVE" | "SUSPENDED" | "PERMANENTLY_DISABLED";

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  ALL: "All Statuses",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  PERMANENTLY_DISABLED: "Permanently Disabled",
};

const getStatusFilterValue = (
  user: ChamberStaffUserViewModel
): Exclude<StatusFilter, "ALL"> =>
  isPermanentlyDisabledStaffUser(user)
    ? "PERMANENTLY_DISABLED"
    : user.accountStatus === "ACTIVE"
      ? "ACTIVE"
      : "SUSPENDED";

// CMP Dashboard (24): a deleted employee's row is grey (#F7F7F8) with the
// status in #E24C4C; its email is erased, so the row shows a dash there.
const PERMANENTLY_DISABLED_ROW_CLASS = "staff-row-permanently-disabled bg-[#F7F7F8]";

// CMP Dashboard (24) CD:9977-9982 (users table Actions): a 112px column with
// 8 px side padding and a 13px/400 button with 6px 10px padding. The old
// 14px/800 MUI button was 9 px wider than the cell at the 1120 px table
// minimum, so it was cut off at 390 and 1280 (live check Oct 31).
const employeeActionsButtonSx = {
  backgroundColor: "#FFFFFF",
  borderColor: "#9E9E9E",
  borderRadius: "8px",
  color: "#0A0A0A",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 13,
  fontWeight: 400,
  gap: "6px",
  lineHeight: "normal",
  minWidth: 0,
  padding: "6px 10px",
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-endIcon": { margin: 0 },
} as const;

const EmployeeActionsChevron = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0A0A0A"
    strokeWidth="2"
    strokeLinecap="round"
    style={{ width: 14, height: 14, flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const formatLastLogin = (value?: number | null) =>
  value ? cmpLongDateTimeFormatter.format(new Date(value)) : "Never";

const statusLabel = (user: ChamberStaffUserViewModel) =>
  STATUS_FILTER_LABELS[getStatusFilterValue(user)];

const statusDotClassName = (user: ChamberStaffUserViewModel) =>
  isPermanentlyDisabledStaffUser(user)
    ? "bg-[#E24C4C]"
    : user.accountStatus === "ACTIVE"
      ? "bg-emerald-500"
      : "bg-red-500";

const getRowActions = (
  user: ChamberStaffUserViewModel,
  loading: boolean
): RowActionDescriptor<StaffUserAction>[] =>
  isPermanentlyDisabledStaffUser(user)
    ? [
        {
          key: "viewPermissions",
          label: "View Past Employee Permissions",
          disabled: loading,
        },
      ]
    : [
  {
    key: "update",
    label: "Update Employee Permissions",
    disabled: loading,
  },
  {
    key: "credentials",
    label: getStaffCredentialsActionLabel(staffCredentialsNeverIssued(user)),
    disabled: loading,
  },
  user.accountStatus === "ACTIVE"
    ? {
        key: "suspend",
        label: "Suspend Employee Access",
        disabled: loading,
      }
    : {
        key: "enable",
        label: "Unsuspend Employee Access",
        disabled: loading,
      },
  {
    key: "delete",
    label: "Delete Employee Access",
    destructive: true,
    dividerBefore: true,
    disabled: loading,
  },
];

export const StaffUsersTable = ({
  users,
  loading,
  error,
  actionLoadingUserId,
  onRetry,
  onAction,
}: StaffUsersTableProps) => {
  const noun = useCmpOrganizationNoun();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [permissionFilter, setPermissionFilter] = useState("ALL");
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [permissionAnchor, setPermissionAnchor] =
    useState<HTMLElement | null>(null);
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null);
  const [selectedUser, setSelectedUser] =
    useState<ChamberStaffUserViewModel | null>(null);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const matchesSearch =
          !normalizedSearch ||
          `${user.firstName} ${user.lastName} ${user.email} ${user.jobTitle}`
            .toLowerCase()
            .includes(normalizedSearch);
        const matchesStatus =
          statusFilter === "ALL" || getStatusFilterValue(user) === statusFilter;
        const matchesPermission =
          permissionFilter === "ALL" ||
          user.permissions.chamberMemberPro.includes(permissionFilter) ||
          user.permissions.chamberPerksAppAdminConsole.includes(
            permissionFilter
          );

        return matchesSearch && matchesStatus && matchesPermission;
      }),
    [normalizedSearch, permissionFilter, statusFilter, users]
  );

  const columns = useMemo<
    DataTableColumn<ChamberStaffUserViewModel, StaffUserColumn>[]
  >(
    () => [
      {
        key: "name",
        header: "NAME",
        width: "16%",
        render: (user) => (
          <span className="font-extrabold text-gray-950">
            {user.firstName} {user.lastName}
          </span>
        ),
      },
      {
        key: "jobTitle",
        header: "JOB TITLE",
        width: "15%",
        render: (user) => user.jobTitle || "—",
      },
      {
        key: "email",
        header: "EMAIL",
        width: "22%",
        render: (user) =>
          isPermanentlyDisabledStaffUser(user) ? (
            "—"
          ) : (
            <span className="break-all">{user.email}</span>
          ),
      },
      {
        key: "permissions",
        header: "PERMISSIONS",
        width: "12%",
        // CMP Dashboard (24): the count opens the view-only permission list.
        render: (user) => (
          <button
            type="button"
            onClick={() => onAction("viewPermissions", user)}
            className="cursor-pointer border-0 bg-transparent p-0 text-left text-[14px] text-[#0A0A0A] underline underline-offset-[3px] hover:text-[#E1A731]"
          >
            {`${getSelectedPermissionCount(user.permissions)} ${
              getSelectedPermissionCount(user.permissions) === 1
                ? "Permission"
                : "Permissions"
            }`}
          </button>
        ),
      },
      {
        key: "status",
        // The S2 header is wider than this column when the table is at its
        // 1120 px minimum (390 live check), so it may wrap like the design
        // header cells instead of running into LAST LOGIN. The span's own
        // white-space beats the table's inherited header nowrap.
        header: <span className="whitespace-normal">EMPLOYEE STATUS</span>,
        // 12% (was 11%) = the design status share (.7fr); at the 1120 px
        // table minimum (390) it still fits the dot + "Permanently".
        width: "12%",
        // "Permanently Disabled" is wider than this column at 1920, so the
        // label wraps between words (design status cell: min-width 0, no
        // nowrap, no break inside a word) and the dot stays on the first line.
        render: (user) => (
          <span
            className={`inline-flex items-start gap-2 break-normal text-sm font-semibold ${
              isPermanentlyDisabledStaffUser(user)
                ? "text-[#E24C4C]"
                : "text-gray-700"
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full ${statusDotClassName(user)}`}
            />
            {statusLabel(user)}
          </span>
        ),
      },
      {
        key: "lastLogin",
        header: "LAST LOGIN",
        width: "13%",
        render: (user) =>
          isPermanentlyDisabledStaffUser(user)
            ? "—"
            : formatLastLogin(user.lastLoginAtUtcMilli),
      },
      {
        key: "actions",
        header: "ACTIONS",
        width: "10%",
        stickyRight: true,
        stickyRightSx: {
          [`tr.staff-row-permanently-disabled > &`]: {
            backgroundColor: "#F7F7F8",
          },
        },
        // Design: the header and the button are centred; the body cell has
        // 8 px side padding (the shared table default is 12 px).
        headerCellSx: { fontWeight: 800, textAlign: "center" },
        cellSx: {
          paddingLeft: "8px !important",
          paddingRight: "8px !important",
          textAlign: "center",
        },
        render: (user) => (
          <RowActionsTrigger
            ariaLabel={`Actions for ${user.firstName} ${user.lastName}`}
            onOpen={(anchorEl) => {
              setSelectedUser(user);
              setActionAnchor(anchorEl);
            }}
            endIcon={<EmployeeActionsChevron />}
            sx={employeeActionsButtonSx}
          />
        ),
      },
    ],
    [onAction]
  );

  const hasActiveFilters =
    Boolean(search) ||
    statusFilter !== "ALL" ||
    permissionFilter !== "ALL";

  return (
    <>
      {error ? (
        <Alert
          severity="error"
          className="mt-5"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      <FilterBar>
        <FilterSearchField
          value={search}
          onChange={setSearch}
          placeholder="Search by name or email"
          ariaLabel="Search employees by name or email"
        />
        <FilterMenuButton
          label={
            statusFilter === "ALL"
              ? "Filter by Employee Status"
              : STATUS_FILTER_LABELS[statusFilter]
          }
          active={statusFilter !== "ALL"}
          onOpen={setStatusAnchor}
          // Design shows the full label; 220px cut it to "Filter by Employ..."
          wrapperClassName="xl:w-[284px]"
        />
        <FilterMenuButton
          label={
            permissionFilter === "ALL"
              ? "Filter by Permission"
              : applyCmpOrganizationNoun(getPermissionLabel(permissionFilter), noun)
          }
          active={permissionFilter !== "ALL"}
          onOpen={setPermissionAnchor}
          wrapperClassName="xl:w-[260px]"
        />
        <ClearFiltersButton
          disabled={!hasActiveFilters}
          onClick={() => {
            setSearch("");
            setStatusFilter("ALL");
            setPermissionFilter("ALL");
          }}
          wrapperClassName="xl:w-[200px]"
        />
      </FilterBar>

      <AnchoredMenu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
      >
        {(
          ["ALL", "ACTIVE", "SUSPENDED", "PERMANENTLY_DISABLED"] as const
        ).map((status) => (
          <MenuItem
            key={status}
            onClick={() => {
              setStatusFilter(status);
              setStatusAnchor(null);
            }}
          >
            <Radio checked={statusFilter === status} size="small" />
            {STATUS_FILTER_LABELS[status]}
          </MenuItem>
        ))}
      </AnchoredMenu>

      <AnchoredMenu
        anchorEl={permissionAnchor}
        open={Boolean(permissionAnchor)}
        onClose={() => setPermissionAnchor(null)}
        PaperProps={{ sx: { maxHeight: 420, maxWidth: 360 } }}
      >
        <MenuItem
          onClick={() => {
            setPermissionFilter("ALL");
            setPermissionAnchor(null);
          }}
        >
          <Radio checked={permissionFilter === "ALL"} size="small" />
          All Permissions
        </MenuItem>
        {ALL_PERMISSION_LEAVES.map((permission) => (
          <MenuItem
            key={permission.value}
            onClick={() => {
              setPermissionFilter(permission.value);
              setPermissionAnchor(null);
            }}
          >
            <Radio
              checked={permissionFilter === permission.value}
              size="small"
            />
            <span className="whitespace-normal">
              {applyCmpOrganizationNoun(permission.label, noun)}
            </span>
          </MenuItem>
        ))}
      </AnchoredMenu>

      <DataTable
        ariaLabel={`${noun.Singular} employees`}
        columns={columns}
        rows={filteredUsers}
        getRowKey={(user) => user.id}
        rowClassName={(user) =>
          isPermanentlyDisabledStaffUser(user)
            ? PERMANENTLY_DISABLED_ROW_CLASS
            : "hover:bg-gray-50"
        }
        showLoadingRow={loading && users.length === 0}
        loadingRowContent={
          <div className="py-8 text-center text-sm font-semibold text-gray-500">
            Loading employees...
          </div>
        }
        showEmptyRow={!loading && !error && filteredUsers.length === 0}
        emptyRowContent={
          <div className="py-8 text-center text-sm font-semibold text-gray-500">
            {hasActiveFilters
              ? "No employees match the current search and filters."
              : "No employees have been created yet."}
          </div>
        }
      />

      <RowActionMenu
        actions={
          selectedUser
            ? getRowActions(
                selectedUser,
                actionLoadingUserId === selectedUser.id
              )
            : []
        }
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor && selectedUser)}
        onClose={() => {
          setActionAnchor(null);
          setSelectedUser(null);
        }}
        onAction={(action) => {
          if (selectedUser) {
            onAction(action, selectedUser);
          }
        }}
      />
    </>
  );
};

export default StaffUsersTable;
